import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { modifier } from 'ember-modifier';
import Modal from './modal';
import type { ModalSize } from './modal';
import Button from './button';
import type { ButtonIntent } from './button';
import AlertBanner from './alert-banner';
import { PhWarning, PhTrash, PhCheckCircle, PhInfo } from 'ember-phosphor-icons';

/**
 * Visual intent for the confirmation dialog. Maps onto the canonical
 * --warning / --danger / --success / --info token recipes plus the
 * matching Button intent for the confirm button.
 */
export type ConfirmationIntent = 'warning' | 'danger' | 'success' | 'info';

/**
 * Which Button @intent fires the confirm action for each ConfirmationIntent.
 * Reuses the canonical `ButtonIntent` union from button.gts (rather than
 * declaring a parallel union) so future Button intent additions / renames
 * propagate here automatically.
 */
const CONFIRM_BUTTON_INTENT: Record<ConfirmationIntent, ButtonIntent> = {
  warning: 'warning',
  danger: 'danger',
  success: 'success',
  info: 'primary',
};

export interface ConfirmationDialogSignature {
  Args: {
    /** Controls whether the dialog is visible */
    isOpen: boolean;
    /** The title displayed in the dialog header */
    title: string;
    /** Max-width of the dialog panel @default "md" */
    size?: ModalSize;
    /** Visual intent of the dialog
     * - 'warning': Amber styling with warning icon
     * - 'danger': Red styling with trash icon
     * - 'success': Emerald styling with check icon
     * - 'info': Blue styling with info icon
     * @default 'warning'
     */
    intent?: ConfirmationIntent;
    /** Text for the confirm button @default 'Confirm' */
    confirmText?: string;
    /** Text for the cancel button @default 'Cancel' */
    cancelText?: string;
    /** User must type this exact text to enable confirm button */
    typeToConfirm?: string;
    /** Hint text for type-to-confirm input */
    typeToConfirmHint?: string;
    /** Externally disable the confirm button (e.g. a required acknowledgment is unchecked) */
    confirmDisabled?: boolean;
    /** Shows loading state on confirm button */
    isRunning?: boolean;
    /** Error message to display (triggers shake animation) */
    error?: string | null;
    /** Called when the user confirms */
    onConfirm: () => void | Promise<void>;
    /** Called when the user cancels or closes */
    onCancel: () => void;
  };
  Blocks: {
    /** Main content of the dialog body */
    default: [];
    /** Optional warning message above main content */
    warning?: [];
  };
  Element: HTMLDivElement;
}

/**
 * ConfirmationDialog — a polished confirmation overlay with:
 * - Gradient header per intent (light wash of the canonical --warning /
 *   --danger / --success / --info token recipes, blended toward the
 *   elevated surface via color-mix)
 * - Animated icon per intent (warning, trash, check, info)
 * - Optional type-to-confirm with intent-colored focus ring
 * - Shake animation on error
 * - Loading state with reimagined Button's three-dot animation
 *
 * Built on the `.app-confirmation*` semantic class family — the intent
 * wrapper exposes `--conf-accent / --conf-bg / --conf-border` and every
 * inner section composes against those custom properties.
 *
 * @example Basic warning
 * ```gts
 * import ConfirmationDialog from 'tio-ui/components/confirmation-dialog';
 *
 * <ConfirmationDialog
 *   @isOpen={{this.showConfirm}}
 *   @title="Archive Application"
 *   @intent="warning"
 *   @onConfirm={{this.handleArchive}}
 *   @onCancel={{this.closeConfirm}}
 * >
 *   This will archive the application. You can restore it later.
 * </ConfirmationDialog>
 * ```
 *
 * @example Danger with type-to-confirm
 * ```gts
 * <ConfirmationDialog
 *   @isOpen={{this.showDelete}}
 *   @title="Delete Record"
 *   @intent="danger"
 *   @typeToConfirm="DELETE"
 *   @onConfirm={{this.handleDelete}}
 *   @onCancel={{this.closeDelete}}
 * >
 *   This action cannot be undone.
 * </ConfirmationDialog>
 * ```
 */
export default class ConfirmationDialog extends Component<ConfirmationDialogSignature> {
  @tracked confirmationInput = '';
  @tracked private _shaking = false;

  /**
   * Modifier that watches @error — when it changes to a new truthy value,
   * triggers the shake animation for 500ms.
   */
  watchError = modifier((_element: HTMLElement, [error]: [string | null | undefined]) => {
    if (error) {
      this._shaking = true;
      const timer = setTimeout(() => {
        this._shaking = false;
      }, 500);

      return () => clearTimeout(timer);
    }
    return undefined;
  });

  get intent(): ConfirmationIntent {
    return this.args.intent ?? 'warning';
  }

  get size() {
    return this.args.size ?? 'md';
  }

  get confirmButtonIntent(): ButtonIntent {
    return CONFIRM_BUTTON_INTENT[this.intent];
  }

  get confirmText() {
    return this.args.confirmText ?? 'Confirm';
  }

  get cancelText() {
    return this.args.cancelText ?? 'Cancel';
  }

  get typeToConfirmHint() {
    if (this.args.typeToConfirmHint) {
      return this.args.typeToConfirmHint;
    }
    return `Type "${this.args.typeToConfirm}" to confirm`;
  }

  get isConfirmDisabled() {
    if (this.args.isRunning) return true;
    if (this.args.confirmDisabled) return true;
    if (this.args.typeToConfirm) {
      return this.confirmationInput.trim() !== this.args.typeToConfirm;
    }
    return false;
  }

  handleInputChange = (event: Event) => {
    this.confirmationInput = (event.target as HTMLInputElement).value;
  };

  handleConfirm = () => {
    this.confirmationInput = '';
    this.args.onConfirm();
  };

  handleCancel = () => {
    this.confirmationInput = '';
    this.args.onCancel();
  };

  <template>
    <Modal
      @isOpen={{@isOpen}}
      @onClose={{this.handleCancel}}
      @size={{this.size}}
      @closeOnEscapeKey={{true}}
      @allowCloseButton={{false}}
      data-test-confirmation-dialog
      ...attributes
    >
      {{! Wrapper carries the intent class — children compose against
          --conf-accent / --conf-bg / --conf-border. `display: contents`
          on .app-confirmation keeps the modal's own flex layout intact. }}
      <div class="app-confirmation app-confirmation--{{this.intent}}">
        {{! Header — intent gradient + icon + title }}
        <div data-test-confirmation-header class="app-confirmation-header">
          <div class="app-confirmation-icon" data-test-confirmation-icon>
            {{#if (eq this.intent "danger")}}
              <PhTrash @size={{22}} />
            {{else if (eq this.intent "success")}}
              <PhCheckCircle @size={{22}} />
            {{else if (eq this.intent "info")}}
              <PhInfo @size={{22}} />
            {{else}}
              <PhWarning @size={{22}} />
            {{/if}}
          </div>
          <h3 data-test-confirmation-title class="app-confirmation-title">
            {{@title}}
          </h3>
        </div>

        {{! Body with optional shake animation }}
        <div
          data-test-confirmation-body
          class="app-confirmation-body {{if this._shaking 'reimagined-shake'}}"
          {{this.watchError @error}}
        >
          {{#if (has-block "warning")}}
            <AlertBanner @intent="warning">
              {{yield to="warning"}}
            </AlertBanner>
          {{/if}}

          <div class="app-confirmation-message">
            {{yield}}
          </div>

          {{#if @typeToConfirm}}
            <label class="app-confirmation-input-label">
              <span class="app-confirmation-input-hint">{{this.typeToConfirmHint}}</span>
              <input
                type="text"
                value={{this.confirmationInput}}
                {{on "input" this.handleInputChange}}
                placeholder={{@typeToConfirm}}
                class="app-input app-input--md font-mono"
                data-test-confirmation-input
              />
            </label>
          {{/if}}

          {{#if @error}}
            <AlertBanner @intent="warning" data-test-confirmation-error>
              {{@error}}
            </AlertBanner>
          {{/if}}
        </div>

        {{! Footer — cancel / confirm buttons }}
        <div data-test-confirmation-footer class="app-confirmation-footer">
          <Button
            @appearance="outlined"
            @intent="default"
            @isDisabled={{@isRunning}}
            data-test-confirmation-cancel
            {{on "click" this.handleCancel}}
          >
            {{this.cancelText}}
          </Button>
          <Button
            @intent={{this.confirmButtonIntent}}
            @isRunning={{@isRunning}}
            @isDisabled={{this.isConfirmDisabled}}
            data-test-confirmation-confirm
            {{on "click" this.handleConfirm}}
          >
            {{this.confirmText}}
          </Button>
        </div>
      </div>
    </Modal>
  </template>
}
